import React, { useState, useCallback } from 'react'
import { X } from 'lucide-react'
import Button from '@/components/ui/Button'
import Checkbox from '@/components/ui/Checkbox'
import MemberSelect from '@/components/ui/MemberSelect'
import { designTokens } from '@/utils/design-tokens'
import { TaskService } from '@/api'
import type { TaskBulkStakeholderReplaceRequest } from '@/api/generated/models/TaskBulkStakeholderReplaceRequest'
import { useAppSelector } from '@/store/hooks'
import { useAlert } from '@/components/ui/Alert/exports'

interface BulkStakeholderModalProps {
  isOpen: boolean
  onClose: () => void
  onSuccess: () => void
}

interface StakeholderReplacement {
  from_member_id: string | null
  to_member_id: string
}

const ROLE_OPTIONS = [
  { key: 'assignee', label: '담당자' },
  { key: 'sub_assignee', label: '부담당자' },
  { key: 'manager', label: '관리자' },
  { key: 'reviewer', label: '검토자' },
] as const

type RoleKey = typeof ROLE_OPTIONS[number]['key']

const BulkStakeholderModal: React.FC<BulkStakeholderModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
}) => {
  const selectedWorkspace = useAppSelector(state => state.workspace.selectedWorkspace)
  const { showSuccess, showError } = useAlert()

  // 역할 선택 상태
  const [selectedRoles, setSelectedRoles] = useState<Set<RoleKey>>(new Set())

  // 기존 멤버 (from)
  const [fromMemberId, setFromMemberId] = useState<string | null>(null)
  const [fromMemberName, setFromMemberName] = useState('')
  const [assignEmptyRoles, setAssignEmptyRoles] = useState(false)

  // 새 멤버 (to)
  const [toMemberId, setToMemberId] = useState('')
  const [toMemberName, setToMemberName] = useState('')

  const [isSubmitting, setIsSubmitting] = useState(false)

  const resetForm = useCallback(() => {
    setSelectedRoles(new Set())
    setFromMemberId(null)
    setFromMemberName('')
    setAssignEmptyRoles(false)
    setToMemberId('')
    setToMemberName('')
  }, [])

  const handleClose = useCallback(() => {
    resetForm()
    onClose()
  }, [resetForm, onClose])

  const handleRoleToggle = useCallback((roleKey: RoleKey) => {
    setSelectedRoles(prev => {
      const next = new Set(prev)
      if (next.has(roleKey)) {
        next.delete(roleKey)
      } else {
        next.add(roleKey)
      }
      return next
    })
  }, [])

  const handleFromMemberSelect = useCallback((memberId: string, memberName: string) => {
    setFromMemberId(memberId || null)
    setFromMemberName(memberName)
    if (memberId) {
      setAssignEmptyRoles(false)
    }
  }, [])

  const handleToMemberSelect = useCallback((memberId: string, memberName: string) => {
    setToMemberId(memberId)
    setToMemberName(memberName)
  }, [])

  const handleAssignEmptyToggle = useCallback(() => {
    setAssignEmptyRoles(prev => {
      const next = !prev
      if (next) {
        setFromMemberId(null)
        setFromMemberName('')
      }
      return next
    })
  }, [])

  const isFormValid = selectedRoles.size > 0 && toMemberId && (fromMemberId || assignEmptyRoles)

  const handleSubmit = useCallback(async () => {
    if (!isFormValid || !selectedWorkspace?.id) return

    setIsSubmitting(true)

    try {
      const replacement: StakeholderReplacement = {
        from_member_id: assignEmptyRoles ? null : fromMemberId,
        to_member_id: toMemberId,
      }

      const requestBody: TaskBulkStakeholderReplaceRequest = {
        workspace_id: selectedWorkspace.id,
        ...(selectedRoles.has('assignee') && { assignee: replacement }),
        ...(selectedRoles.has('sub_assignee') && { sub_assignee: replacement }),
        ...(selectedRoles.has('manager') && { manager: replacement }),
        ...(selectedRoles.has('reviewer') && { reviewer: replacement }),
      }

      const response = await TaskService.appsTaskViewsBulkReplaceStakeholders({
        requestBody,
      })

      const roleLabels = Array.from(selectedRoles).map(
        key => ROLE_OPTIONS.find(r => r.key === key)?.label
      ).join(', ')

      showSuccess(`${roleLabels} 일괄 수정 완료 (${response.updated_count}건 변경)`)
      handleClose()
      onSuccess()
    } catch (err) {
      console.error('담당자 일괄 수정 실패:', err)
      showError('담당자 일괄 수정에 실패했습니다.')
    } finally {
      setIsSubmitting(false)
    }
  }, [isFormValid, selectedWorkspace, selectedRoles, fromMemberId, toMemberId, assignEmptyRoles, handleClose, onSuccess, showSuccess, showError])

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50"
        onClick={handleClose}
      />

      {/* Modal */}
      <div
        className="relative bg-white rounded-lg shadow-xl w-full max-w-[520px] mx-4"
        style={{ fontFamily: designTokens.typography.fontFamily.sans[0] }}
      >
        {/* Header */}
        <div
          className="flex items-center justify-between px-6 py-4 border-b"
          style={{ borderBottomColor: designTokens.colors.gray[1] }}
        >
          <h2
            style={{
              fontSize: designTokens.typography.fontSize['title-small'][0],
              fontWeight: 700,
              color: designTokens.colors.gray[6],
            }}
          >
            담당자 일괄 수정
          </h2>
          <button
            onClick={handleClose}
            className="p-1 rounded hover:bg-gray-100 transition-colors"
          >
            <X className="w-5 h-5" style={{ color: designTokens.colors.gray[4] }} />
          </button>
        </div>

        {/* Body */}
        <div className="px-6 py-5 space-y-5">
          {/* 역할 선택 */}
          <div>
            <label
              className="block mb-2"
              style={{
                fontSize: designTokens.typography.fontSize['body-medium'][0],
                fontWeight: 600,
                color: designTokens.colors.gray[6],
              }}
            >
              변경할 역할 선택
            </label>
            <div className="flex flex-wrap gap-3">
              {ROLE_OPTIONS.map(role => (
                <label
                  key={role.key}
                  className="flex items-center gap-2 cursor-pointer select-none"
                >
                  <Checkbox
                    checked={selectedRoles.has(role.key)}
                    onChange={() => handleRoleToggle(role.key)}
                    size="medium"
                  />
                  <span
                    style={{
                      fontSize: designTokens.typography.fontSize['body-medium'][0],
                      color: designTokens.colors.gray[6],
                    }}
                  >
                    {role.label}
                  </span>
                </label>
              ))}
            </div>
          </div>

          {/* 기존 멤버 선택 */}
          <div>
            <label
              className="block mb-2"
              style={{
                fontSize: designTokens.typography.fontSize['body-medium'][0],
                fontWeight: 600,
                color: designTokens.colors.gray[6],
              }}
            >
              기존 멤버
            </label>

            {/* 빈 역할 지정 옵션 */}
            <label className="flex items-center gap-2 mb-2 cursor-pointer select-none">
              <Checkbox
                checked={assignEmptyRoles}
                onChange={handleAssignEmptyToggle}
                size="medium"
              />
              <span
                style={{
                  fontSize: designTokens.typography.fontSize['body-small'][0],
                  color: designTokens.colors.gray[5],
                }}
              >
                비어있는 역할에 신규 지정
              </span>
            </label>

            {!assignEmptyRoles && (
              <MemberSelect
                workspaceId={selectedWorkspace?.id || ''}
                selectedMemberId={fromMemberId}
                selectedMemberName={fromMemberName || '기존 멤버 선택'}
                onSelect={handleFromMemberSelect}
                placeholder="기존 멤버 선택"
                label="기존 멤버"
              />
            )}
          </div>

          {/* 새 멤버 선택 */}
          <div>
            <label
              className="block mb-2"
              style={{
                fontSize: designTokens.typography.fontSize['body-medium'][0],
                fontWeight: 600,
                color: designTokens.colors.gray[6],
              }}
            >
              새 멤버
            </label>
            <MemberSelect
              workspaceId={selectedWorkspace?.id || ''}
              selectedMemberId={toMemberId}
              selectedMemberName={toMemberName || '새 멤버 선택'}
              onSelect={handleToMemberSelect}
              placeholder="새 멤버 선택"
              label="새 멤버"
            />
          </div>

          {/* 안내 문구 */}
          <div
            className="p-3 rounded"
            style={{
              backgroundColor: designTokens.colors.gray[0],
              fontSize: designTokens.typography.fontSize['body-small'][0],
              color: designTokens.colors.gray[5],
              lineHeight: '1.5',
            }}
          >
            {assignEmptyRoles
              ? '선택한 역할이 비어있는 모든 과제에 새 멤버가 지정됩니다.'
              : '선택한 역할에서 기존 멤버가 배정된 모든 과제를 새 멤버로 교체합니다.'}
          </div>
        </div>

        {/* Footer */}
        <div
          className="flex items-center justify-end gap-2 px-6 py-4 border-t"
          style={{ borderTopColor: designTokens.colors.gray[1] }}
        >
          <Button
            size="medium"
            color="white"
            onClick={handleClose}
            disabled={isSubmitting}
          >
            취소
          </Button>
          <Button
            size="medium"
            color="dark"
            onClick={handleSubmit}
            disabled={!isFormValid || isSubmitting}
          >
            {isSubmitting ? '처리 중...' : '일괄 수정'}
          </Button>
        </div>
      </div>
    </div>
  )
}

export default BulkStakeholderModal
